/* eslint-disable camelcase */
import { services } from '~/config/constants';
import API from './api';

const { authenticationService, coreService, userService } = services;

export const login = async (email: string, password: string, device_id: string, token?: string) => {
    return await API({
        endpoint: `${authenticationService}/login`,
        method: 'POST',
        data: {
            email: email.toLowerCase(),
            password,
            device_id
        },
        unAuth: true,
        token
    }
    );
};

export const onRefreshToken = async (refresh_token: string) => {
    return await API(
        {
            endpoint: `${authenticationService}/token/refresh-token`,
            method: 'POST',
            data: {
                refresh_token
            },
            unAuth: true
        }
    );
};

export const forgotPassword = async (email: string) => {
    return await API(
        {
            endpoint: `${authenticationService}/forgot-password`,
            method: 'POST',
            data: {
                email: email.toLowerCase()
            }
        }

    );
};

export const forgotPasswordOTP = async (email: string, otp: string) => {
    return await API(
        {
            endpoint: `${authenticationService}/forgot-password/${otp}`,
            method: 'POST',
            data: {
                email: email.toLowerCase()
            }
        }
    );
};

export const changePassword = async (id: string, password: string, device_id: string) => {
    return await API(
        {
            endpoint: `${coreService}/${id}/identity/change-password`,
            method: 'POST',
            data: {
                password,
                device_id
            }
        }
    );
};

export const activateRegistrationCode = async (code: string) => {
    return await API(
        {
            endpoint: `${authenticationService}/registration/code/${code}`,
            method: 'POST'
        }
    );
};

export const onCheckOldPassword = async (id: string, password: string) => {
    return await API(
        {
            endpoint: `${userService}/${id}/verify-password`,
            method: 'POST',
            data: {
                password
            }
        }
    );
};

export const onRegisterDevice = async (id: string, device_id: string) => {
    return await API(
        {
            endpoint: `${coreService}/${id}/device`,
            method: 'POST',
            data: {
                device_id
            }
        }
    );
};

export const onGetAppVersion = async (id: string, appName: string, version: string, osType: string, appType: string) => {
    return await API({
        endpoint: `${coreService}/${id}/versions?app_name=${encodeURIComponent(appName)}&version=${encodeURIComponent(version)}&os_type=${encodeURIComponent(osType)}&app_type=${encodeURIComponent(appType)}`
    });
};

export const onChangePasswordRequest = async (id: string, new_email: string) => {
    return await API({
        endpoint: `${coreService}/${id}/identity/change-email`,
        data: {
            new_email
        },
        method: 'POST'
    });
};

export const onChangePasswordValidate = async (id: string, otp: string, device_id: string, email: string) => {
    return await API({
        endpoint: `${authenticationService}/${id}/identity/change-email-otp-check`,
        data: {
            otp,
            device_id,
            email
        },
        method: 'POST'
    });
};

export const onGetRefreshToken = async () => {
    return await API({
        endpoint: `${authenticationService}/token/refresh-token-from-client-id`,
        method: 'GET',
        unAuth: true
    });
};

export const onDeleteRefreshToken = async (id: string) => {
    return await API({
        endpoint: `${userService}/${id}/token/delete-refresh-token`,
        method: 'DELETE'
    });
};

/**
 * Spend a short-lived handoff token for a real Welfare session.
 *
 * `unAuth` because there is by definition no access token to send — this call
 * is how one is obtained. The request still carries x-client-id, and that
 * matters: the Authentication Service binds the token it mints to the client
 * making *this* request, so the session belongs to the Welfare document rather
 * than to the host that minted the handoff token.
 *
 * @param {String} handoff_token the token to spend
 * @returns {Promise<Object>} the new session: token, refresh_token, api_key
 */
export const onExchangeHandoffToken = async (handoff_token: string) => {
    return await API({
        endpoint: `${authenticationService}/token/handoff/exchange`,
        method: 'POST',
        data: {
            handoff_token
        },
        unAuth: true
    });
};

/**
 * Give back a handoff token that will not be used, because a valid session is
 * already in place.
 *
 * @param {String} handoff_token the token to revoke
 * @returns {Promise<Object>} the service acknowledgement
 */
export const onRevokeHandoffToken = async (handoff_token: string) => {
    return await API({
        endpoint: `${authenticationService}/token/handoff/revoke`,
        method: 'POST',
        data: {
            handoff_token
        },
        unAuth: true
    });
};
